import React from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import InputAdornment from '@material-ui/core/InputAdornment';
import {
  Add,
  Business,
  ChevronRight,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Search,
  Settings,
  SyncAlt,
} from '@material-ui/icons';
import { RouteComponentProps, withRouter } from 'react-router';
import { unpackToDateTime } from '@campfire/hot-date';
import { ConsoleActionButton } from '../../common/ConsoleActionButton';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { GET_ENTERPRISE_SUB_ACCOUNTS } from './enterprise-sub-accounts.gql';
import { CreateSubAccountDialog } from './CreateSubAccountDialog';
import { ShareItemsDialog, ShareTarget } from './ShareItemsDialog';
import { GET_ENTERPRISE_SUB_ACCOUNT_PROGRAMS } from './enterprise-sub-account-programs.gql';
import { formatHeadlineEntityCounts } from './enterprise-entity-labels';
import { EnterpriseSubLockStructures } from './EnterpriseSubLockStructures';
import { EnterpriseSubHiddenPages } from './EnterpriseSubHiddenPages';
import { EnterprisePushDefault } from './EnterprisePushDefault';
import { EnterpriseSubPersonalDashboards } from './EnterpriseSubPersonalDashboards';
import { GetEnterpriseSubAccounts } from './__generated__/GetEnterpriseSubAccounts';
import { GetEnterpriseSubAccountPrograms } from './__generated__/GetEnterpriseSubAccountPrograms';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingLeft: 40,
      paddingRight: 40,
      paddingTop: 20,
      backgroundColor: '#F9F9F9',
      minHeight: 'calc(100% - 20px)',
    },
    heading: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingBottom: '8px',
      borderBottom: '1px solid #efefef',
    },
    headingTitle: {
      color: theme.color.grey.neutralBrand900,
      fontWeight: 600,
      paddingBottom: '5px',
      fontSize: 20,
    },
    containerPanel: {
      background: 'white',
      paddingTop: '1.25rem',
      paddingBottom: '1.25rem',
      paddingLeft: '1.875rem',
      paddingRight: '1.875rem',
      marginTop: '2.25rem',
    },
    panelToggle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
    },
    headingPanel: {
      fontWeight: 600,
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
    },
    bodyPanel: {
      fontSize: '14px',
      color: '#808080',
    },
    tableHead: {
      '& th': {
        fontWeight: 600,
        fontSize: '11px',
        textTransform: 'uppercase' as const,
        color: theme.color.grey.neutral200,
        borderBottom: '1px solid #e0e0e0',
      },
    },
    subName: {
      fontWeight: 600,
      fontSize: '14px',
      color: theme.color.grey.neutralBrand900,
    },
    subDomain: {
      color: theme.color.grey.neutral200,
      fontSize: '12px',
    },
    cellText: {
      fontSize: '13px',
      color: theme.color.grey.neutral400,
    },
    mutedCellText: {
      fontSize: '13px',
      color: theme.color.grey.neutral200,
    },
    pendingBadge: {
      display: 'inline-block',
      minWidth: 22,
      textAlign: 'center' as const,
      borderRadius: 11,
      padding: '2px 8px',
      fontSize: '12px',
      fontWeight: 600,
    },
    pendingNone: {
      backgroundColor: '#efefef',
      color: theme.color.grey.neutral200,
    },
    searchField: {
      width: 200,
      '& .MuiOutlinedInput-root': {
        height: 32,
        borderRadius: 4,
        fontSize: '13px',
        backgroundColor: 'white',
      },
      '& .MuiOutlinedInput-input': {
        paddingTop: 7,
        paddingBottom: 7,
      },
    },
    headerButton: {
      height: 32,
      borderRadius: 4,
      fontSize: '13px',
    },
    searchIcon: {
      fontSize: '16px',
      color: theme.color.grey.neutral200,
    },
    chevron: {
      fontSize: '20px',
      color: theme.color.grey.neutral200,
    },
    rowButton: {
      textTransform: 'none' as const,
      fontSize: '13px',
      whiteSpace: 'nowrap' as const,
    },
    programChips: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: 4,
      maxWidth: 260,
    },
    programChip: {
      height: 22,
      fontSize: '12px',
      backgroundColor: '#efefef',
      color: theme.color.grey.neutral400,
    },
    pendingSome: {
      backgroundColor: '#fff0e0',
      color: '#c77700',
    },
  })
);

const VISIBLE_PROGRAMS = 3;

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return 'Never';
  const dt = unpackToDateTime(dateStr);
  if (!dt.isValid) return 'Never';
  return dt.toFormat('d MMM y');
};

interface ProgramChipsProps {
  names: string[];
  chipsClassName: string;
  chipClassName: string;
  emptyClassName: string;
}

const ProgramChips = ({ names, chipsClassName, chipClassName, emptyClassName }: ProgramChipsProps) => {
  if (names.length === 0) {
    return <Typography className={emptyClassName}>None</Typography>;
  }

  const hidden = names.slice(VISIBLE_PROGRAMS);

  return (
    <Box className={chipsClassName}>
      {names.slice(0, VISIBLE_PROGRAMS).map((name) => (
        <Chip key={name} size='small' label={name} className={chipClassName} />
      ))}
      {hidden.length > 0 && (
        <Tooltip title={hidden.join(', ')}>
          <Chip size='small' label={`+${hidden.length}`} className={chipClassName} />
        </Tooltip>
      )}
    </Box>
  );
};

const AdminConsoleSubAccountsComponent = (props: RouteComponentProps) => {
  const { history, match } = props;
  const classes = useStyles();
  const { isSm } = useCampfireTheme();

  const [showCreateDialog, setShowCreateDialog] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [subAccountsOpen, setSubAccountsOpen] = React.useState(true);
  const [shareWith, setShareWith] = React.useState<string[] | undefined>();

  const { data, loading, refetch } = useCampfireQuery<GetEnterpriseSubAccounts, {}>(GET_ENTERPRISE_SUB_ACCOUNTS);
  const { data: programsData, refetch: refetchPrograms } = useCampfireQuery<GetEnterpriseSubAccountPrograms, {}>(
    GET_ENTERPRISE_SUB_ACCOUNT_PROGRAMS
  );

  const allSubAccounts = data?.tm.enterpriseSubAccounts ?? [];

  const programNamesBySub = (programsData?.vm.enterpriseSubAccountPrograms ?? []).reduce<Record<string, string[]>>(
    (acc, sub) => ({ ...acc, [sub.subTenantId]: sub.programs.map((program) => program.name) }),
    {}
  );

  const shareTargets: ShareTarget[] = allSubAccounts.map((subAccount) => ({
    tenantId: subAccount.tenantId,
    name: subAccount.name,
    programNames: programNamesBySub[subAccount.tenantId] ?? [],
  }));

  const searchTerm = search.trim().toLowerCase();
  const subAccounts =
    searchTerm === ''
      ? allSubAccounts
      : allSubAccounts.filter((subAccount) => subAccount.name.toLowerCase().includes(searchTerm));
  const withPending = subAccounts.filter((subAccount) => subAccount.pendingUpdateCount > 0).length;

  return (
    <Box className={classes.container}>
      <Box className={classes.heading}>
        <Box>
          <Typography variant='h2' className={classes.headingTitle}>
            Sub-Accounts
          </Typography>
          <Typography variant='body2'>{'Manage the accounts that sit beneath this one'}</Typography>
        </Box>
        <ConsoleActionButton
          endIcon={<Add />}
          variant='contained'
          color='primary'
          onClick={() => setShowCreateDialog(true)}
        >
          Create Sub-Account
        </ConsoleActionButton>
      </Box>

      <CreateSubAccountDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onCreated={() => {
          if (refetch) {
            setTimeout(refetch, 0);
          }
        }}
      />

      {shareWith && (
        <ShareItemsDialog
          open
          subAccounts={shareTargets}
          initialSubTenantIds={shareWith}
          onClose={() => setShareWith(undefined)}
          onShared={() => {
            if (refetch) {
              setTimeout(refetch, 0);
            }
            if (refetchPrograms) {
              setTimeout(refetchPrograms, 0);
            }
          }}
        />
      )}

      <Box className={classes.containerPanel}>
        <Box className={classes.panelToggle} onClick={() => setSettingsOpen((open) => !open)}>
          <Box>
            <Typography className={classes.headingPanel}>
              <Settings style={{ fontSize: '14px', paddingRight: '5px' }} /> Sub-Account Settings
            </Typography>
            <Typography variant='body2' className={classes.bodyPanel}>
              {
                'Sub-account admins cannot change these. Once changed here, apply them under Push Updates > Organisation settings.'
              }
            </Typography>
          </Box>
          <IconButton size='small' aria-label={settingsOpen ? 'Hide settings' : 'Show settings'}>
            {settingsOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </Box>
        <Collapse in={settingsOpen} timeout='auto'>
          <Box mt={2}>
            <EnterpriseSubLockStructures />
          </Box>
          <Divider style={{ margin: '20px 0 16px' }} />
          <EnterpriseSubHiddenPages />
          <Divider style={{ margin: '20px 0 16px' }} />
          <EnterpriseSubPersonalDashboards />
          <Divider style={{ margin: '20px 0 16px' }} />
          <EnterprisePushDefault />
        </Collapse>
      </Box>

      {loading && <LinearProgress style={{ marginTop: '1rem' }} />}

      {!loading && allSubAccounts.length === 0 ? (
        <Box className={classes.containerPanel}>
          <Typography className={classes.headingPanel}>
            <Business style={{ fontSize: '14px', paddingRight: '5px' }} /> Create your first sub-account
          </Typography>
          <Typography variant='body2' className={classes.bodyPanel}>
            {
              "You haven't created any sub-accounts yet. Click the 'Create Sub-Account' button top right to add your first."
            }
          </Typography>
        </Box>
      ) : (
        !loading && (
          <Box className={classes.containerPanel}>
            <Box
              display='flex'
              flexDirection={isSm ? 'column' : 'row'}
              alignItems={isSm ? 'left' : 'center'}
              justifyContent='space-between'
              mb='4px'
            >
              <Box className={classes.panelToggle} onClick={() => setSubAccountsOpen((open) => !open)}>
                <Typography className={classes.headingPanel}>
                  <Business style={{ fontSize: '14px', paddingRight: '5px' }} /> Sub-Accounts
                </Typography>
              </Box>
              <Box display='flex' alignItems='center' style={{ gap: 8 }}>
                <Button
                  size='small'
                  variant='outlined'
                  color='primary'
                  disableElevation
                  className={`${classes.rowButton} ${classes.headerButton}`}
                  startIcon={<SyncAlt style={{ fontSize: '16px' }} />}
                  onClick={() => setShareWith([])}
                >
                  Share Items
                </Button>
                {subAccountsOpen && (
                  <TextField
                    variant='outlined'
                    size='small'
                    placeholder='Search sub-accounts'
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    className={classes.searchField}
                    InputProps={{
                      style: { height: 32, fontSize: '13px', borderRadius: 4, backgroundColor: 'white' },
                      inputProps: { style: { paddingTop: 0, paddingBottom: 0 } },
                      startAdornment: (
                        <InputAdornment position='start'>
                          <Search className={classes.searchIcon} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                <IconButton
                  size='small'
                  aria-label={subAccountsOpen ? 'Hide sub-accounts' : 'Show sub-accounts'}
                  onClick={() => setSubAccountsOpen((open) => !open)}
                >
                  {subAccountsOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </IconButton>
              </Box>
            </Box>
            <Typography
              variant='body2'
              className={classes.bodyPanel}
              style={{ marginBottom: subAccountsOpen ? '1rem' : 0 }}
            >
              {`You have ${allSubAccounts.length} sub-account${allSubAccounts.length !== 1 ? 's' : ''}${
                withPending > 0 ? `, ${withPending} with pending updates` : ''
              }.`}
            </Typography>

            <Collapse in={subAccountsOpen} timeout='auto'>
              <TableContainer>
                <Table>
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell>Sub-Account</TableCell>
                      <TableCell>Admins</TableCell>
                      <TableCell>Programs</TableCell>
                      <TableCell>Entities</TableCell>
                      <TableCell>Last Pushed</TableCell>
                      <TableCell align='right'>Pending</TableCell>
                      <TableCell align='right'>{''}</TableCell>
                      <TableCell padding='checkbox'>{''}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subAccounts.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8}>
                          <Typography className={classes.bodyPanel}>{`No sub-accounts match '${search}'.`}</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {subAccounts.map((subAccount) => (
                      <TableRow
                        key={subAccount.tenantId}
                        hover
                        style={{ cursor: 'pointer' }}
                        onClick={() => history.push(`${match.url}/${subAccount.tenantId}`)}
                      >
                        <TableCell>
                          <Typography className={classes.subName}>{subAccount.name}</Typography>
                          <Typography className={classes.subDomain}>{subAccount.fqdn}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography className={classes.cellText}>
                            {subAccount.admins.length === 0
                              ? 'No admins yet'
                              : `${subAccount.admins.length} admin${subAccount.admins.length !== 1 ? 's' : ''}`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <ProgramChips
                            names={programNamesBySub[subAccount.tenantId] ?? []}
                            chipsClassName={classes.programChips}
                            chipClassName={classes.programChip}
                            emptyClassName={classes.mutedCellText}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography className={classes.cellText}>
                            {formatHeadlineEntityCounts(subAccount.mappedEntityCounts)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography className={classes.mutedCellText}>
                            {formatDate(subAccount.lastPushedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell align='right'>
                          <span
                            className={`${classes.pendingBadge} ${
                              subAccount.pendingUpdateCount > 0 ? classes.pendingSome : classes.pendingNone
                            }`}
                          >
                            {subAccount.pendingUpdateCount}
                          </span>
                        </TableCell>
                        <TableCell align='right'>
                          <Button
                            size='small'
                            variant='outlined'
                            color='primary'
                            disableElevation
                            className={classes.rowButton}
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              setShareWith([subAccount.tenantId]);
                            }}
                          >
                            Share Items
                          </Button>
                        </TableCell>
                        <TableCell padding='checkbox'>
                          <ChevronRight className={classes.chevron} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Box>
        )
      )}
    </Box>
  );
};

export const AdminConsoleSubAccounts = withRouter(AdminConsoleSubAccountsComponent);
